import type AjaxRequestBroker from "../AjaxRequestBroker";
import type {ApiOpts} from "@esWebAPI/es/model/Models";

const Cache = global.Cache; // jscache

const $: JQueryStatic = global.$;
const es = global.es; // TODO: remove dependency!

/**
 * @class CacheModule
 * 
 * Provides caching functionality to API meta modules.
 */
export default class CacheModule {
    private readonly cache;
    private readonly enableDebug: boolean;

    enableCache: boolean;

    constructor() {
        this.cache = new Cache();
        this.enableDebug = false && es.isDeveloper;

        this.enableCache = true;
    }

    /**
     * 
     * @param resourceName 
     * @param id 
     * 
     * @returns 
     */
    private getKey(resourceName: string, id: string | number | string[] | number[]) {
        resourceName = resourceName.replaceAll("/", "_");

        if (resourceName.endsWith("_")) {
            resourceName = resourceName.slice(0, -1);
        }

        return resourceName + "_" + id;
    }

    /**
     * Checks the cache (if present) for the given value.
     * If it's there it will return an instantly resolved promise for consistency with ajax gets.
     * 
     * @param resourceName e.g. "LayerInfo", "GeoMap"
     * @param ids used in conjunction resourceName to create a cache key. Can be either an array or a single String castable value. e.g. 5, "MyKey".
     * @param opts 
     * 
     * @returns 
     */
    private checkCache(resourceName: string, ids: string | number | string[] | number [], opts?: ApiOpts): JQueryDeferred<any> | null {

        // TODO: fix this method to always return a promise

        if (opts && (opts.noCache || opts.basicAuth)) {
            //es.log.info("noCache or basicAuth - skip cache!", resourceName, ids);
            return null;
        }

        // if param is an empty array we will not make a server request but just insta resolve to an empty array. may cause a bug - let me know if it does
        if (Array.isArray(ids) && ids.length == 0) {
            return es.util.deferred.resolved([]);
        }

        if (this.enableCache && this.cache) {
            if (resourceName && ids !== 'undefined') {
                // If ids is not an array the put it in an array for simpler processing
                if (Array.isArray(ids)) {
                    // special case: empty array of ids was requested. just resolve to an an empty array immediately
                    if (ids.length == 0) {
                        if (this.enableDebug) {
                            console.info("checkCache : returning :", []);
                        }

                        return es.util.deferred.resolved([]);
                    }

                    const itemArr = [];
                    for (let i = 0; i < ids.length; i++) {
                        let item = this.cache.getItem(this.getKey(resourceName, ids[i]));

                        if (item != null) {
                            item = this.getItemCopy(item);
                            if (Array.isArray(item) && ids.length == 1) {
                                itemArr.push( item[0] ); // temp. fix
                            } else {
                                itemArr.push( item );
                            }
                        }
                    }

                    // Only return if we have a complete match.
                    // Partial matches (i.e. say you request ids 101,102,103 and have 101 and 102 in cache then only need request 103)
                    // can be handled later *if req'd* but the complexity and potential difficulty in troubleshooting would probably
                    // not be worth the effort and/or performance gain.
                    if (itemArr.length == ids.length) {
                        if (this.enableDebug) {
                            console.info("checkCache : returning :", itemArr);
                        }

                        return es.util.deferred.resolved(this.getItemCopy(itemArr));
                    }
                } else {
                    const key = this.getKey(resourceName, ids);
                    const item = this.cache.getItem(key);

                    if (item != null) {
                        if (this.enableDebug) {
                            console.info("checkCache : returning :", item);
                        }

                        return es.util.deferred.resolved(this.getItemCopy(item));
                    } else if (item === null) {
                        if (this.cache.storage_.keys().includes(key)) {
                            //console.info("checkCache : returning explicitly set null for key " + key);
                            if (this.enableDebug) {
                                console.info("checkCache : returning explicitly set null for key " + key);
                            }

                            return es.util.deferred.resolved(null);
                        }
                    }
                }

                // No match
                if (this.enableDebug) {
                    console.info("Cache miss", this.getKey(resourceName, ids));
                }

                return null;
            } else {
                console.warn("es.cache#checkCache:: invalid params: ", resourceName, ids);
            }
        }

        // No match
        if(this.enableDebug) {
            console.info("Cache miss", this.getKey(resourceName, ids));
        }

        return null;
    }

    /**
     * Get item from cache
     * 
     * @param resourceName 
     * @param ids 
     * @param opts 
     * 
     * @returns 
     */
    get(resourceName: string, ids: string | number | string[] | number[], opts?: ApiOpts) {
        return this.checkCache(resourceName, ids, opts);
    }

    /**
     * Add item to cache (if present)
     * 
     * @param resourceName 
     * @param id 
     * @param item 
     * @param opts 
     */
    set(resourceName: string, id: string | number, item: any, opts?: ApiOpts) {
        if (this.enableCache && this.cache) {
            if (resourceName && id != null && id != undefined && item != undefined) {
                if (this.enableDebug) {
                    console.info("set : ", this.getKey(resourceName, id), item);
                }

                this.cache.setItem(this.getKey(resourceName, id), this.getItemCopy(item));
            } else {
                console.warn("es.cache#set :: attempted to set invalid cache item", resourceName, id, item);
            }
        }
    }

    /**
     * Adds a preloaded item to cache
     * 
     * @param key 
     * @param item 
     * @param opts 
     */
    private setPreload(key: string, item: any, opts?: ApiOpts) {
        if (this.enableCache && this.cache) {
            if (key) {
                if (this.enableDebug) {
                    console.info("setPreload : ", key, item);
                }

                this.cache.setItem(key, this.getItemCopy(item), {isPreload: true, preloadHit: 0});
            } else {
                console.error("es.cache#setPreload :: invalid cache key", key, item);
            }
        }
    }

    /**
     * Add entity item to cache. Assumes entity obj has an id attribute
     * 
     * @param resourceName 
     * @param entity 
     * @param opts 
     */
    setEntity(resourceName: string, entity: any, opts?: ApiOpts) {
        if (entity && entity["id"]) {
            this.set(resourceName, entity["id"], entity, opts);
        } else {
            console.warn("es.cache :: attempted to set invalid cache item", entity);
        }
    }

    /**
     * Clear the entire cache
     */
    clearAll() {
        if (this.enableCache && this.cache) {
            if (this.enableDebug) {
                console.info("clearAll");
            }

            this.cache.clear();
        }
    }

    /**
     * Clear the cache for specified resource, id
     * 
     * @param resourceName 
     * @param id 
     * 
     * @returns 
     */
    clear(resourceName: string, id?: string | number) {
        if (this.enableCache && this.cache) {
            if (!id) {
                if (this.enableDebug) {
                    console.info("clearResource", resourceName);
                }

                this.cache.removeWhere((k, v) => k.indexOf(resourceName + "_") === 0); // if key begins with resourceName then clear it (will also blitz lists)
                return;
            }

            if (this.enableDebug) {
                console.info("clearEntity", resourceName, id);
                console.info("clearEntity : check cache cascade for entity ", resourceName, id);
            }

            this.cache.removeWhere((k, v) => {
                return k === this.getKey(resourceName, id) || k === resourceName + "_list"; // if whole key matches or there's a list stored
            });
        }
    }

    /**
     * Prints stats about the cache to console
     */
    getStats() {
        console.info("Cache size: ", this.cache.size());
        console.info("Cache stats: ", this.cache.getStats());
        console.info("Cache contents: ", this.cache.storage_.items_);
        //console.info("Preload hits: ", this.cache.getStats());

        return this.cache;
    }

    /**
     * Performs a PUT operation updates the cache if action is successful.
     * Non-caching equivalent would be: rqBroker.doPut({url: url, data: data});
     * 
     * @param rqBroker
     * @param params object that would normally be 1st param passed to rqBroker.put
     * @param resourceName
     * @param id used in conjunction resourceName to create a cache key. Must be a single String-castable value. e.g. 5, "MyKey".
     *        If omitted then the id property of the response object will be used i.e. it will be assumed that your object extends com.espatial.es.serverapi.MetaDataEntity,
     *        which has an id.
     *        Note: if you updating an object which requires the cache invalidation of other objects (e.g. child or parent objects) then this must be specifically handled
     *        within the calling meta module method. e.g. if removing a Workspace object then es.api.workspace.remove should also purge cache of related LayerViews.
     *        TODO: maybe we should just do such processing here internally in the cache? would mean cache is tightly coupled to api but maybe that cost is worth it...
     * @param opts 
     * 
     * @returns dfd.promise
     */
    doPut(rqBroker, params, resourceName: string, id, opts?: ApiOpts): JQueryDeferred<any> {
        return rqBroker.doPut( params ).done((rs) => {
            if (id) {
                this.set(resourceName, id, rs, opts);
            } else {
                this.setEntity(resourceName, rs, opts);
            }
        });
    }

    /**
     * Check if cache exists and if it contains the resource specified. If not, request from server and add to cache.
     * Non-caching equivalent would be: rqBroker.doGet({url: url});
     * 
     * @param rqBroker 
     * @param resourceName 
     * @param url 
     * @param ids 
     * @param opts 
     * 
     * @returns dfd.promise
     */
    doGet(rqBroker: AjaxRequestBroker, resourceName: string, url: string, ids, opts?: ApiOpts): JQueryDeferred<any> {
        // TODO: allow opts override default error handler and caching
        return this.checkCache(resourceName, ids, opts) || rqBroker.doGet( {url: url, opts: opts} ).done((rs) => {
            // Don't cache any null get values (even though they might be validly so)
            if (this.enableCache && rs != null) {
                if (Array.isArray(ids) && Array.isArray(rs)) {
                    if( ids.length === 1 && rs.length === 1) {
                        // special case to handle array of ids with just one id which returns an array
                        //TODO this doesn't cover the case when a Thematics has only 1 thematic as this will return
                        // that only Thematic and not an array.
                        this.set(resourceName, ids[0], rs[0], opts);
                    } else {
                        for (let i = 0; i < ids.length; i++) {
                            this.set(resourceName, ids[i], rs[i], opts); // length of ids and rs should be the same (i think)
                        }
                    }
                //} else if(typeof rs == "boolean" || typeof rs == "number" || typeof rs == "string"){
                //    ; // going to try caching these responses
                } else {
                    this.set(resourceName, ids, rs, opts);
                }
            }
        });
    }

    /**
     * Check if cache exists and if it contains the resource specified. If not, request from server and add to cache.
     * Non-caching equivalent would be: rqBroker.doGet({url: url});
     * @return dfd.promise
     */
    //_.doGet = function(rqBroker, resourceName, url, ids, opts){
    doGetList(rqBroker, resourceName: string, criteria, sortOrder, offset?: number, length?: number, usedAfter?: number, opts?: ApiOpts) {
        const url = es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length, null, null, usedAfter);

        // TODO: Only caching lists w/o any criteria for time being. This will need to change soon
        if (criteria || sortOrder || offset || length || usedAfter) {
            return rqBroker.doGet( {url: url, opts: opts} );
        } else {
            return this.checkCache(resourceName, "list", opts) || rqBroker.doGet( {url: url, opts: opts} ).done((rs) => {
                // Don't cache any null get values (even though they might be validly so)
                if (rs != null && Array.isArray(rs)) {
                    // Add individuals and list... must remember to blitz list as well whenever individual entities are blitzed!
                    for (let i = 0; i < rs.length; i++) {
                        this.set(resourceName, rs[i].id, rs[i], opts);
                    }
                    this.set(resourceName, "list", rs, opts);
                } else {
                    if (this.enableDebug) {
                        console.warn("es.cache#doGetList :: unable to cache response getList response");
                    }
                }
            });
        }
    };

    /**
     * 
     * @param item 
     * 
     * @returns 
     */
    private getItemCopy(item:any): any {
        const itemCopy = es.util.object.deepCopy(item);

        if (this.enableDebug) {
            console.info("checkCache : getItemCopy : cloned item ", item, itemCopy);
        }

        return itemCopy;
    }

    /**
     * Disable use of preloading for the session.
     */
    disablePreLoad() {
        $.sessionStorage.setItem("es_preload", "false");
        console.info("client-side preload cache disabled");
    }

    /**
     * Enable preloading for the session.
     * Note: preloading is enabled by default so this is only useful if disablePreLoad has been called
     */
    enablePreLoad() {
        $.sessionStorage.removeItem("es_preload");
        console.info("client-side preload cache enabled");
    }

    /**
     * Check if preloading is enabled
     * 
     * @returns
     */
    isPreLoadEnabled(): boolean {
        if (es.util.url.getParam("preload") == "false" || $.sessionStorage.getItem("es_preload") == "false") {
            return false;
        }

        return true;
    }

    /**
     * 
     */
    initPreLoad() {
        // if url contains preload=false then do not process the preload
        if (!this.isPreLoadEnabled()) {
            return;
        }

        // process cache preloads
        if (es.preload) {
            //console.info("cache.js, es.preload", es.preload);
            for (let k in es.preload) {
                this.setPreload(k, es.preload[k]);
            }
            //console.info("cache.js, es.preload", cache.storage_.items_);
        }

        if (es.isDeveloper === true && this.enableCache){ // && this.enableDebug
            $(window).on('beforeunload', () => {
                this.getStats();
            });

            // check if all preload items have been accessed. if not, flag the inefficiency (in developer mode only)
            window.setTimeout(() => {
                const unusedPreloads = [];
                for (let k in this.cache.storage_.items_) {
                    const item = this.cache.storage_.items_[k];

                    if (item.options['isPreload'] && item.options['preloadHit'] < 1 && item.value != null) { // nulls get a pass - not always correct. require change to cache lib
                        unusedPreloads.push(item);
                    }
                }

                if (unusedPreloads.length > 0) {
                    es.log.warn("Try to only preload resources that will be used by the page as loads. unusedPreloadCount == ", unusedPreloads.length, "\nunusedPreloads", unusedPreloads);
                }

                // TODO: this tally may not be true accurate as the count will also include hits for an rq/rs that ws not preloaded but made and subsequently hit in the usual way
                //       - Could get correct count by iterating over es.api._cache().getStats().storage_.items_ and add up preloadHit counts
                es.log.info("Preload cache preload stats after 10 seconds: ", this.cache.getStats(), "cache map: ", this.cache.storage_.items_);
            }, 10000);
        }
    }
}
